import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppHeader from '@/components/common/AppHeader'
import AppFooter from '@/components/common/AppFooter'
import Spinner from '@/components/common/Spinner'
import { useAuth } from '@/stores/auth'
import { useUI } from '@/stores/ui'
import {
  architectApi, type ArchDaily, type ArchLog, type ArchMetrics, type ArchParish, type ArchPrimaryAdmin,
  type FlagRule, type MessageFlag,
} from '@/api/endpoints/architect'

/**
 * Architect Page (directives/Architect Page.md): software administration for the Architect only.
 * Parishes, Primary Admins, flagged messages + flag rules, logs, metrics, settings.
 */
type Tab = 'parishes' | 'admins' | 'flags' | 'rules' | 'logs' | 'metrics' | 'settings'
const TABS: Array<[Tab, string]> = [
  ['parishes', 'Parishes'], ['admins', 'Primary Admins'], ['flags', 'Flagged messages'], ['rules', 'Flag rules'],
  ['logs', 'Logs'], ['metrics', 'Metrics'], ['settings', 'Settings'],
]

const fmt = (iso?: string | null) => (iso ? new Date(iso + (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? '' : 'Z')).toLocaleString() : '—')
const fmtDate = (iso?: string | null) => (iso ? iso.slice(0, 10) : '—')
const errMsg = (e: any, fallback: string) => e?.response?.data?.detail && typeof e.response.data.detail === 'string' ? e.response.data.detail : fallback

/** Instant filter: every word typed must appear somewhere in the row's values. */
function useFilter<T extends object>(rows: T[], q: string) {
  return useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean)
    if (!words.length) return rows
    return rows.filter((r) => {
      const hay = Object.values(r).map((v) => (v == null ? '' : String(v))).join(' ').toLowerCase()
      return words.every((w) => hay.includes(w))
    })
  }, [rows, q])
}

function FilterBox({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <input className="input-field w-full sm:w-80" placeholder="Type to filter…" aria-label={label}
      value={value} onChange={(e) => onChange(e.target.value)} />
  )
}

// ------------------------------------------------------------------------------------------- parishes
function ParishesTab() {
  const addToast = useUI((s) => s.addToast)
  const [rows, setRows] = useState<ArchParish[] | null>(null)
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<string | null>(null)
  const [purgeText, setPurgeText] = useState('')
  const [busy, setBusy] = useState(false)
  const load = useCallback(async () => setRows(await architectApi.parishes()), [])
  useEffect(() => { load().catch(() => setRows([])) }, [load])
  const filtered = useFilter(rows || [], q)
  const p = rows?.find((r) => r.id === sel) || null
  const act = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true)
    try { await fn(); addToast({ message: ok, type: 'success' }); await load() }
    catch (e) { addToast({ message: errMsg(e, 'Action failed'), type: 'error' }) }
    finally { setBusy(false) }
  }
  const locked = (rows || []).filter((r) => r.is_locked)

  if (!rows) return <Spinner label="Loading parishes…" />
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <FilterBox value={q} onChange={setQ} label="Filter parishes" />
        <button className="btn-secondary text-sm" onClick={() => architectApi.exportAddresses()}>Export parish addresses (CSV)</button>
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded-xl">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr><th className="p-2">Parish</th><th className="p-2">Address</th><th className="p-2">Primary Admin</th>
              <th className="p-2">Billing</th><th className="p-2">People</th><th className="p-2">State</th></tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} onClick={() => { setSel(r.id); setPurgeText('') }} aria-label={`Parish ${r.name}`}
                className={`border-t cursor-pointer hover:bg-yellow-50 ${sel === r.id ? 'bg-yellow-100' : ''} ${r.is_deleted ? 'text-gray-400' : ''}`}>
                <td className="p-2"><div className="font-medium">{r.name}</div><div className="text-xs text-gray-500">{r.sharecode}</div></td>
                <td className="p-2">{[r.address, r.city, r.state, r.zip_code].filter(Boolean).join(', ') || '—'}</td>
                <td className="p-2">{r.primary_admin_name}<div className="text-xs text-gray-500">{r.primary_admin_email}</div></td>
                <td className="p-2">{r.subscription_status}{r.auto_renew ? ' · auto-renew' : ''}</td>
                <td className="p-2 text-xs">{r.users} users · {r.students} students · {r.classes} classes</td>
                <td className="p-2 text-xs">
                  {r.is_deleted ? <span>Deleting {fmtDate(r.purge_on)}</span> : r.is_locked ? <span className="text-red-600 font-semibold">Locked</span> : 'Open'}
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td className="p-3 text-gray-500" colSpan={6}>No parishes match.</td></tr>}
          </tbody>
        </table>
      </div>

      {p && (
        <div className="p-4 border-2 border-yellow-300 rounded-xl bg-yellow-50/40 space-y-3" aria-label="Selected parish actions">
          <div className="font-semibold">Selected: {p.name}</div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary text-sm" disabled={busy} onClick={() => act(() => architectApi.exportParish(p.id), 'Download started')}>Download parish data</button>
            {p.is_locked
              ? <button className="btn-secondary text-sm" disabled={busy} onClick={() => act(() => architectApi.unlock(p.id), 'Parish unlocked')}>Unlock parish</button>
              : <button className="btn-secondary text-sm" disabled={busy} onClick={() => act(() => architectApi.lock(p.id), 'Parish locked')}>Lock parish</button>}
            {p.is_deleted
              ? <button className="btn-secondary text-sm" disabled={busy} onClick={() => act(() => architectApi.undelete(p.id), 'Parish undeleted')}>Undelete</button>
              : <button className="btn-secondary text-sm" disabled={busy} onClick={() => act(() => architectApi.deleteLater(p.id), 'Parish will be deleted in 90 days')}>Delete parish (90 day delay)</button>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input className="input-field text-sm w-40" placeholder='Type "delete"' aria-label="Type delete to confirm"
              value={purgeText} onChange={(e) => setPurgeText(e.target.value)} />
            <button className="btn-secondary text-sm text-red-700 border-red-300 disabled:opacity-50" aria-label="Delete parish NOW"
              disabled={busy || purgeText.trim().toLowerCase() !== 'delete'}
              onClick={() => act(async () => { await architectApi.purge(p.id, purgeText); setSel(null) }, 'Parish permanently deleted')}>
              Delete parish NOW
            </button>
            <span className="text-xs text-gray-500">Permanent: removes the parish, its classes, students, parents and users.</span>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-semibold mb-2">Locked parishes</h3>
        {locked.length ? (
          <ul className="text-sm list-disc pl-5">{locked.map((r) => <li key={r.id}>{r.name} ({r.sharecode}){r.locked_reason ? ` — ${r.locked_reason}` : ''}</li>)}</ul>
        ) : <p className="text-sm text-gray-500">None.</p>}
        <p className="text-xs text-gray-500 mt-1">Unlock from the list above.</p>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------------------------- primary admins
function AdminsTab() {
  const addToast = useUI((s) => s.addToast)
  const [rows, setRows] = useState<ArchPrimaryAdmin[] | null>(null)
  const [q, setQ] = useState('')
  const [temp, setTemp] = useState<{ email: string; pw: string } | null>(null)
  useEffect(() => { architectApi.primaryAdmins().then(setRows).catch(() => setRows([])) }, [])
  const filtered = useFilter(rows || [], q)
  if (!rows) return <Spinner label="Loading primary admins…" />
  return (
    <div className="space-y-4">
      <FilterBox value={q} onChange={setQ} label="Filter primary admins" />
      {temp && (
        <div className="p-3 rounded-lg border border-green-300 bg-green-50 text-sm" role="status">
          New temporary password for {temp.email}: <code className="font-mono font-bold">{temp.pw}</code> (also emailed to them).
        </div>
      )}
      <div className="overflow-x-auto border border-gray-200 rounded-xl">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr><th className="p-2">First</th><th className="p-2">Last</th><th className="p-2">Account ID</th><th className="p-2">Status</th>
              <th className="p-2">Parish</th><th className="p-2">Sharecode</th><th className="p-2">Created</th><th className="p-2">Last login</th><th className="p-2" /></tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={`${r.user_id}-${r.parish_id}`} className="border-t">
                <td className="p-2">{r.first_name}</td><td className="p-2">{r.last_name}</td>
                <td className="p-2 font-mono text-xs">{r.account_number}</td>
                <td className="p-2">{r.subscription_status || '—'}{r.parish_locked ? ' · locked' : ''}{r.parish_deleted ? ' · deleting' : ''}</td>
                <td className="p-2">{r.parish_name || '—'}</td><td className="p-2">{r.sharecode || '—'}</td>
                <td className="p-2 text-xs">{fmtDate(r.created_at)}</td><td className="p-2 text-xs">{fmt(r.last_login)}</td>
                <td className="p-2">
                  <button className="btn-secondary text-xs" aria-label={`Reset password for ${r.email}`}
                    onClick={async () => {
                      if (!confirm(`Reset the password for ${r.email}? They'll get a temporary one by email.`)) return
                      try { const res = await architectApi.resetPassword(r.user_id); setTemp({ email: r.email, pw: res.temporary_password }) }
                      catch (e) { addToast({ message: errMsg(e, 'Reset failed'), type: 'error' }) }
                    }}>Reset password</button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td className="p-3 text-gray-500" colSpan={9}>No primary admins match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------------------------ flagged messages
function FlagsTab() {
  const [rows, setRows] = useState<MessageFlag[] | null>(null)
  const [all, setAll] = useState(false)
  const load = useCallback(async () => setRows(await architectApi.flags(!all)), [all])
  useEffect(() => { load().catch(() => setRows([])) }, [load])
  if (!rows) return <Spinner label="Loading flagged messages…" />
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Include reviewed</label>
      {!rows.length && <p className="text-sm text-gray-500">No flagged messages{all ? '' : ' waiting for review'}.</p>}
      {rows.map((f) => (
        <div key={f.id} className={`p-3 border rounded-xl ${f.reviewed_at ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}>
          <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-600">
            <span><strong className="uppercase">{f.priority || '—'}</strong> · {f.rule_name} · keyword “{f.keyword}”</span>
            <span>{fmt(f.message_created_at || f.flagged_at)}</span>
          </div>
          <p className="my-2 whitespace-pre-wrap">{f.body}</p>
          <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-600">
            <span>From {f.sender_name || 'unknown'}{f.conversation_name ? ` in “${f.conversation_name}”` : ''}{f.event === 'message.edited' ? ' (edited)' : ''}</span>
            {!f.reviewed_at
              ? <button className="btn-secondary text-xs" onClick={async () => { await architectApi.reviewFlag(f.id); await load() }}>Mark reviewed</button>
              : <span>Reviewed {fmt(f.reviewed_at)}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}

// ------------------------------------------------------------------------------------------ flag rules
function RulesTab() {
  const addToast = useUI((s) => s.addToast)
  const [rows, setRows] = useState<FlagRule[] | null>(null)
  const [form, setForm] = useState({ name: '', keywords: '', match_type: 'exact', priority: 'medium' })
  const load = useCallback(async () => setRows((await architectApi.flagRules()).filter((r) => r.is_active)), [])
  useEffect(() => { load().catch(() => setRows([])) }, [load])
  if (!rows) return <Spinner label="Loading flag rules…" />
  return (
    <div className="space-y-4">
      <form className="p-4 border rounded-xl grid gap-2 sm:grid-cols-5 items-end" onSubmit={async (e) => {
        e.preventDefault()
        const keywords = form.keywords.split(',').map((k) => k.trim()).filter(Boolean)
        if (!form.name.trim() || !keywords.length) { addToast({ message: 'Name and at least one keyword', type: 'error' }); return }
        try {
          await architectApi.createFlagRule({ name: form.name.trim(), keywords, match_type: form.match_type, priority: form.priority })
          setForm({ ...form, name: '', keywords: '' }); await load(); addToast({ message: 'Rule added', type: 'success' })
        } catch (err) { addToast({ message: errMsg(err, 'Could not add rule'), type: 'error' }) }
      }}>
        <label className="text-sm">Name<input className="input-field w-full" aria-label="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="text-sm sm:col-span-2">Keywords (comma-separated)<input className="input-field w-full" aria-label="Rule keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} /></label>
        <label className="text-sm">Match
          <select className="input-field w-full" aria-label="Match type" value={form.match_type} onChange={(e) => setForm({ ...form, match_type: e.target.value })}>
            <option value="exact">Whole word</option><option value="contains">Contains</option><option value="regex">Regex</option>
          </select>
        </label>
        <label className="text-sm">Priority
          <select className="input-field w-full" aria-label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
          </select>
        </label>
        <button className="btn-primary sm:col-span-5 justify-self-start" type="submit">Add rule</button>
      </form>
      {!rows.length && <p className="text-sm text-gray-500">No rules yet. Messages are only flagged when a rule matches.</p>}
      <ul className="divide-y border rounded-xl">
        {rows.map((r) => (
          <li key={r.id} className="p-3 flex flex-wrap justify-between gap-2 text-sm">
            <span><strong>{r.name}</strong> · {r.match_type === 'exact' ? 'whole word' : r.match_type} · {r.priority}<br />
              <span className="text-gray-600">{r.keywords.join(', ')}</span></span>
            <button className="btn-secondary text-xs" aria-label={`Remove rule ${r.name}`} onClick={async () => {
              if (!confirm(`Remove the rule "${r.name}"?`)) return
              await architectApi.deleteFlagRule(r.id); await load()
            }}>Remove</button>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ----------------------------------------------------------------------------------------------- logs
function LogsTab() {
  const [rows, setRows] = useState<ArchLog[] | null>(null)
  const [q, setQ] = useState('')
  useEffect(() => { architectApi.logs({ limit: 1000 }).then(setRows).catch(() => setRows([])) }, [])
  const filtered = useFilter(rows || [], q)
  if (!rows) return <Spinner label="Loading logs…" />
  return (
    <div className="space-y-3">
      <FilterBox value={q} onChange={setQ} label="Filter logs" />
      <p className="text-xs text-gray-500">Newest first (latest 1000). Filter by user ID, account number, email, action or text.</p>
      <div className="overflow-x-auto border border-gray-200 rounded-xl max-h-[60vh]">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-left text-gray-600 sticky top-0">
            <tr><th className="p-2">When (UTC)</th><th className="p-2">User</th><th className="p-2">Action</th><th className="p-2">Details</th></tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-t align-top">
                <td className="p-2 whitespace-nowrap">{l.at?.replace('T', ' ').slice(0, 19)}</td>
                <td className="p-2">{l.user_email}<div className="text-gray-500 font-mono">{l.user_account}</div></td>
                <td className="p-2 font-mono">{l.action}</td><td className="p-2">{l.description}</td>
              </tr>
            ))}
            {!filtered.length && <tr><td className="p-3 text-gray-500" colSpan={4}>No log entries match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// -------------------------------------------------------------------------------------------- metrics
function MetricsTab() {
  const [asOf, setAsOf] = useState('')
  const [m, setM] = useState<ArchMetrics | null>(null)
  const [d, setD] = useState<ArchDaily | null>(null)
  const [range, setRange] = useState({ from: '', to: '' })
  useEffect(() => { architectApi.metrics(asOf || undefined).then(setM).catch(() => setM(null)) }, [asOf])
  useEffect(() => { architectApi.daily(range.from || undefined, range.to || undefined).then(setD).catch(() => setD(null)) }, [range])
  const tile = (label: string, v: number | null | undefined) => (
    <div className="p-3 border rounded-xl"><div className="text-xs text-gray-500">{label}</div><div className="text-xl font-semibold">{v ?? 'not tracked'}</div></div>
  )
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm">
        <label htmlFor="asof">Snapshot as of</label>
        <input id="asof" type="date" className="input-field" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        {asOf && <button className="btn-secondary text-xs" onClick={() => setAsOf('')}>Today</button>}
      </div>
      {m ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {tile('Parishes registered', m.parishes_registered)}{tile('Active subscriptions', m.subscriptions_active)}
          {tile('On auto-renewal', m.parishes_auto_renew)}{tile('Locked parishes', m.parishes_locked)}
          {tile('Forever free', m.subscriptions_forever_free)}{tile('First year free', m.subscriptions_first_year_free)}
          {tile('First year discounted', m.subscriptions_first_year_discounted)}{tile('Students', m.students)}
          {Object.entries(m.users_by_role).map(([k, v]) => <div key={k}>{tile(k.replace('_', ' ') + 's', v)}</div>)}
          {tile('Class events', m.class_events)}
        </div>
      ) : <Spinner label="Loading metrics…" />}
      {m && Object.keys(m.role_descriptions).length > 0 && (
        <div className="text-sm"><h3 className="font-semibold mb-1">Roles</h3>
          {Object.entries(m.role_descriptions).map(([k, v]) => <span key={k} className="mr-4">{k}: <strong>{v}</strong></span>)}
        </div>
      )}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <h3 className="font-semibold mr-2">Per day</h3>
          <label>From <input type="date" className="input-field" aria-label="From date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></label>
          <label>To <input type="date" className="input-field" aria-label="To date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></label>
        </div>
        {d && (
          <div className="overflow-x-auto border rounded-xl max-h-80">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-left sticky top-0"><tr><th className="p-2">Date</th><th className="p-2">Parish registrations</th><th className="p-2">User registrations</th><th className="p-2">Class events created</th></tr></thead>
              <tbody>{[...d.days].reverse().map((r) => (
                <tr key={r.date} className="border-t"><td className="p-2">{r.date}</td><td className="p-2">{r.parish_registrations}</td><td className="p-2">{r.user_registrations}</td><td className="p-2">{r.class_events_created}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {d && <p className="text-xs text-gray-500">Not tracked yet: {d.not_tracked.map((x) => x.replace(/_/g, ' ')).join(', ')}.</p>}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------------------------------- settings
function SettingsTab() {
  const addToast = useUI((s) => s.addToast)
  const [email, setEmail] = useState('')
  const [def, setDef] = useState('')
  useEffect(() => { architectApi.settings().then((s) => { setEmail(s.contact_email || ''); setDef(s.contact_email_default) }).catch(() => {}) }, [])
  return (
    <form className="max-w-lg space-y-2" onSubmit={async (e) => {
      e.preventDefault()
      try { await architectApi.saveSettings(email.trim() || null); addToast({ message: 'Saved', type: 'success' }) }
      catch (err) { addToast({ message: errMsg(err, 'Could not save'), type: 'error' }) }
    }}>
      <label className="block text-sm font-medium" htmlFor="contact-email">Contact us email</label>
      <input id="contact-email" type="email" className="input-field w-full" value={email} placeholder={def} onChange={(e) => setEmail(e.target.value)} />
      <p className="text-xs text-gray-500">Where the public Contact Us form sends messages. Empty = {def || 'the default'}.</p>
      <button className="btn-primary" type="submit">Save</button>
    </form>
  )
}

export default function ArchitectPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('parishes')
  useEffect(() => { if (user && user.role !== 'ARCHITECT') navigate('/dashboard', { replace: true }) }, [user, navigate])
  if (!user || user.role !== 'ARCHITECT') return null
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppHeader />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-1">Architect</h1>
        <p className="text-sm text-gray-500 mb-4">Software administration — not visible to parish users.</p>
        <div className="flex flex-wrap gap-1 border-b mb-4" role="tablist">
          {TABS.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              className={`px-3 py-2 text-sm rounded-t-lg ${tab === k ? 'bg-white border border-b-white -mb-px font-semibold' : 'text-gray-600 hover:text-gray-900'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="bg-white p-4 rounded-xl border">
          {tab === 'parishes' && <ParishesTab />}
          {tab === 'admins' && <AdminsTab />}
          {tab === 'flags' && <FlagsTab />}
          {tab === 'rules' && <RulesTab />}
          {tab === 'logs' && <LogsTab />}
          {tab === 'metrics' && <MetricsTab />}
          {tab === 'settings' && <SettingsTab />}
        </div>
      </main>
      <AppFooter />
    </div>
  )
}
